import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/styles/PopularProjects.css"; // external CSS
import analyticsService from "../../services/analyticsService";
import propertyService from "../../services/propertyService";

import LeftNav from "../components/common/LeftNav";
import TopNav from "../components/common/TopNav";
import LocationIcon from "../assets/img/adminloc.svg"
import newlead from "../assets/img/newleads.svg"
import BookingIcon from "../assets/img/Booklead.svg";
import LeadIcon from "../assets/img/registerlead.svg";
import EyeIcon from "../assets/img/eye.svg";
import arrowleftt from "../assets/img/arrowleftt.svg"

// Fallback image (place one in public/assets/img/project1.jpg)
import modernimg from "../assets/img/modernbuilding.svg";

export default function PopularProjects() {
  const navigate = useNavigate();
  // EXACT React Native app state structure
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [states, setStates] = useState([]);
  const [citiesByState, setCitiesByState] = useState({});
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  // Project thumbnails keyed by id — popular-projects API only returns images[0]
  const [thumbnails, setThumbnails] = useState({});

  useEffect(() => { loadInitialData(); }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const citiesStatesResponse = await analyticsService.getCitiesAndStates();
      if (citiesStatesResponse.success && citiesStatesResponse.data) {
        setStates([...new Set((citiesStatesResponse.data.states || []).map(s => s.replace(/\s+\d{5,6}\s*$/, '').trim()))].sort());
        setCitiesByState(citiesStatesResponse.data.citiesByState || {});
      }
      await loadProjects();
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async (pageNum = 1) => {
    try {
      const filters = { page: pageNum, per_page: 20 };
      if (selectedCity) filters.city = selectedCity;
      if (selectedState) filters.state = selectedState;
      const response = await analyticsService.getPopularProjects(filters);
      if (response.success && response.data) {
        const items = response.data.items || [];
        setProjects(items);
        loadThumbnails(items);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
        setPage(pageNum);
      } else {
        setProjects([]); setTotal(0); setTotalPages(1); setPage(1);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]); setTotal(0); setTotalPages(1);
    }
  };

  // Fetch each project's thumbnail (same priority as the portal: thumbnail, then images[0])
  const loadThumbnails = async (items) => {
    const entries = await Promise.all(
      items.filter((p) => p.id).map(async (p) => {
        const res = await propertyService.getPropertyById(p.id);
        return [p.id, res?.success ? res.data?.thumbnail || null : null];
      })
    );
    setThumbnails((prev) => ({ ...prev, ...Object.fromEntries(entries.filter(([, t]) => t)) }));
  };

  const handleApply = async () => {
    setLoading(true);
    await loadProjects(1);
    setLoading(false);
  };

  const handleStateChange = (e) => { setSelectedState(e.target.value); setSelectedCity(''); };
  const handleCityChange = (e) => { setSelectedCity(e.target.value); };
  const getFilteredCities = () => !selectedState ? [] : (citiesByState[selectedState] || []);

  // Image URL helper (EXACT React Native app logic)
  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sandbox.rebax.in';
    return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

   return (
    <div className="pb-page">
      <LeftNav />
      <div className="pb-content-wrap">
        <TopNav />

        {/* 🔹 PopularProjects content starts here */}
        <div className="pp2-page">
          {/* Header */}
          <div className="pp2-header">
            <div className="pp2-header-left">
               <img src={arrowleftt} onClick={() => navigate("/webadmindashboard")} style={{cursor: "pointer"}}></img>
              <h2 className="pp2-title">Popular Projects</h2>
            </div>

            <div className="pp2-filters">
              <div className="pp2-filter">
                <label className="pp2-label">State</label>
                <select
                  className="pp2-select"
                  value={selectedState}
                  onChange={handleStateChange}
                  disabled={loading}
                >
                  <option value="">Select State</option>
                  {Array.isArray(states) ? states.map((state, idx) => (
                    <option key={idx} value={state}>{state}</option>
                  )) : []}
                </select>
              </div>

              {selectedState && (
                <div className="pp2-filter">
                  <label className="pp2-label">City</label>
                  <select
                    className="pp2-select"
                    value={selectedCity}
                    onChange={handleCityChange}
                    disabled={loading}
                  >
                    <option value="">Select City</option>
                    {getFilteredCities().map((city, idx) => (
                      <option key={idx} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              )}

              <button className="pp2-apply-btn" onClick={handleApply} disabled={loading}>
                {loading ? 'Loading...' : 'Apply'}
              </button>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
            </div>
          ) : projects.length === 0 ? (
            <div className="pp2-empty-container">
              <p>No projects found</p>
              <button onClick={handleApply} className="pp2-retry-btn">Retry</button>
            </div>
          ) : (
            <>
              {/* Grid */}
              <div className="pp2-grid">
                {projects.map((project, idx) => {
                  const imageUrl = getFullImageUrl(thumbnails[project.id] || project.project_image);
                  const location = [project.locality, project.city].filter(Boolean).join(', ');
                  const rank = (page - 1) * 20 + idx + 1;

                  return (
                    <div className="pp2-card" key={project.id || idx}>
                      <div className="pp2-img-wrap">
                        <img
                          src={imageUrl || modernimg}
                          alt={project.project_name || 'Unknown Project'}
                          className="pp2-img"
                          onError={(e) => (e.currentTarget.src = modernimg)}
                        />
                      </div>
                      <div className="pp2-card-body">
                        <h6
                          className="pp2-card-title"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin-projectdetails/${project.id || project._id}`, {
                              state: {
                                project: {
                                  id: project?.id || project?._id || null,
                                  _id: project?._id || project?.id || null,
                                  title: project?.title || project?.property_title || project?.name || "Property",
                                  property_title: project?.property_title || project?.title || project?.name || "Property",
                                  location: project?.location || project?.address || project?.locality || project?.city || "",
                                  address: project?.address || project?.location || "",
                                  city: project?.city || "",
                                  locality: project?.locality || "",
                                  builder_id: project?.builder_id || project?.user_id || null,
                                  user_id: project?.user_id || project?.builder_id || null,
                                  thumbnail: project?.thumbnail || project?.project_logo || null,
                                  project_logo: project?.project_logo || project?.thumbnail || null,
                                  images: Array.isArray(project?.images) ? project.images.slice(0, 8) : [],
                                  distances: Array.isArray(project?.distances) ? project.distances.slice(0, 4) : [],
                                }
                              }
                            });
                          }}
                        >
                          <span className="pp2-rank">{rank}</span> {project.project_name || 'Unknown Project'}
                        </h6>
                        <ul className="pp2-meta-grid">
                          <li className="pp2-meta-cell">
                            <img src={LocationIcon} alt="" className="pp2-meta-icon" />
                            <span className="pp2-meta-label">{location || 'N/A'}</span>
                          </li>

                          <li className="pp2-meta-cell">
                            <img src={BookingIcon} alt="" className="pp2-meta-icon" />
                            <span className="pp2-meta-label"><strong className="pp2-num">{project.total_bookings || 0}</strong> Total Bookings</span>
                          </li>

                          <li className="pp2-meta-cell">
                            <img src={LeadIcon} alt="" className="pp2-meta-icon" />
                            <span className="pp2-meta-label"><strong className="pp2-num">{project.registered_leads || 0}</strong> Registered Leads</span>
                          </li>

                          <li className="pp2-meta-cell">
                            <img src={newlead} alt="" className="pp2-meta-icon" />
                            <span className="pp2-meta-label"><strong className="pp2-num">{project.new_leads || 0}</strong> New Leads</span>
                          </li>

                          <li className="pp2-meta-cell">
                            <img src={EyeIcon} alt="" className="pp2-meta-icon" />
                            <span className="pp2-meta-label"><strong className="pp2-num">{project.project_views || 0}</strong> Project Views</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination (EXACT React Native app logic) */}
              {totalPages > 1 && (
                <div className="pp2-pagination">
                  <button
                    className={`pp2-page-btn ${page <= 1 ? 'disabled' : ''}`}
                    onClick={async () => {
                      if (page > 1 && !loading) {
                        setLoading(true);
                        try {
                          await loadProjects(page - 1);
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                    disabled={page <= 1 || loading}
                  >
                    ← Previous
                  </button>

                  <span className="pp2-page-info">
                    Page {page} of {totalPages} ({total} total)
                  </span>

                  <button
                    className={`pp2-page-btn ${page >= totalPages ? 'disabled' : ''}`}
                    onClick={async () => {
                      if (page < totalPages && !loading) {
                        setLoading(true);
                        try {
                          await loadProjects(page + 1);
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                    disabled={page >= totalPages || loading}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        {/* 🔹 PopularProjects content ends here */}
      </div>
    </div>
  );

}